import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SecurityIcon from '@mui/icons-material/Security';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Fade,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
  useTheme,
  Zoom,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageTransition from '../components/PageTransition';
import { useCart } from '../contexts/CartContext';
import ProductService, { Product } from '../services/productService';

const features = [
  {
    icon: <LocalShippingIcon fontSize="large" />,
    title: 'Livraison gratuite',
    description: 'Pour toute commande supérieure à 20€',
  },
  {
    icon: <SecurityIcon fontSize="large" />,
    title: 'Paiement sécurisé',
    description: 'Transactions 100% sécurisées',
  },
  {
    icon: <SupportAgentIcon fontSize="large" />,
    title: 'Support client',
    description: 'Assistance 7j/7',
  },
];

const categories = [
  { id: 'all', name: 'Toutes les catégories' },
  { id: 'cards', name: 'Cartes de lecture' },
  { id: 'accessories', name: 'Accessoires' },
  { id: 'bundles', name: 'Packs' },
  { id: 'new', name: 'Nouveautés' },
];

export const StorePage: React.FC = () => {
  const { addToCart } = useCart();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [hoveredCard, setHoveredCard] = React.useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const handleAutoLogin = async () => {
      const params = new URLSearchParams(location.search);
      const token = params.get('app_token');

      if (token) {
        // Stocker les informations d'authentification
        localStorage.setItem('access_token', token);
      }
    };

    handleAutoLogin();
  }, [location, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await ProductService.getAllProducts();
        setProducts(data);
      } catch (err) {
        setError('Erreur lors du chargement des produits');
        console.error('Erreur:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const filteredProducts = products.filter((product) => {
    if (selectedCategory === 'all') return true;
    if (selectedCategory === 'new') return product.isNew;
    return product.category === selectedCategory;
  });

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <PageTransition>
      <Box
        sx={{
          py: 4,
          background: `linear-gradient(135deg, ${theme.palette.background.default}, ${theme.palette.background.paper})`,
          minHeight: '100vh',
        }}
      >
        <Container maxWidth="lg">
          {/* Hero Section */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 8 },
              mb: 6,
              borderRadius: 4,
              position: 'relative',
              overflow: 'hidden',
              minHeight: { xs: '200px', md: '300px' },
              display: 'flex',
              alignItems: 'center',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'url("/assets/img/happy-little-girl.png")',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'brightness(0.7)',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `linear-gradient(135deg,rgba(194, 183, 183, 0.6),rgba(139, 126, 126, 0.6))`,
              },
            }}
          >
            <Fade in={true} timeout={1000}>
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  width: '100%',
                  textAlign: 'center',
                  color: 'white',
                }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 800,
                    mb: 3,
                    textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }}
                >
                  Découvrez notre collection
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 4,
                    opacity: 0.9,
                    fontWeight: 400,
                    lineHeight: 1.6,
                    maxWidth: '800px',
                    mx: 'auto',
                  }}
                >
                  Des cartes de lecture uniques pour une expérience musicale exceptionnelle. Design
                  premium, qualité garantie.
                </Typography>
              </Box>
            </Fade>
          </Paper>

          {/* Features Section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 3,
              mb: 8,
              justifyContent: 'space-between',
            }}
          >
            {features.map((feature, index) => (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  minWidth: { xs: '100%', md: '30%' },
                }}
              >
                <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      borderRadius: 4,
                      bgcolor: 'background.paper',
                      border: `1px solid ${theme.palette.divider}`,
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 8px 24px 0 rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        color: theme.palette.primary.main,
                        mb: 2,
                        p: 2,
                        borderRadius: '50%',
                        bgcolor: theme.palette.primary.main + '10',
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Paper>
                </Zoom>
              </Box>
            ))}
          </Box>

          {/* Main Content with Categories */}
          <Box sx={{ display: 'flex', gap: 4 }}>
            {/* Categories Sidebar */}
            <Paper
              elevation={0}
              sx={{
                width: 280,
                p: 2,
                borderRadius: 4,
                border: `1px solid ${theme.palette.divider}`,
                display: { xs: 'none', md: 'block' },
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, px: 2 }}>
                Catégories
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List>
                {categories.map((category) => (
                  <ListItem key={category.id} disablePadding>
                    <ListItemButton
                      selected={selectedCategory === category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      sx={{
                        borderRadius: 2,
                        mb: 1,
                        '&.Mui-selected': {
                          bgcolor: theme.palette.primary.main + '10',
                          '&:hover': {
                            bgcolor: theme.palette.primary.main + '20',
                          },
                        },
                      }}
                    >
                      <ListItemText
                        primary={category.name}
                        sx={{
                          '& .MuiTypography-root': {
                            fontWeight: selectedCategory === category.id ? 600 : 400,
                            color:
                              selectedCategory === category.id ? 'primary.main' : 'text.primary',
                          },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>

            {/* Products Section */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h4"
                component="h2"
                gutterBottom
                sx={{
                  mb: 4,
                  fontWeight: 'bold',
                  textAlign: 'center',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 60,
                    height: 3,
                    bgcolor: theme.palette.primary.main,
                    borderRadius: 1.5,
                  },
                }}
              >
                {categories.find((cat) => cat.id === selectedCategory)?.name || 'Nos Produits'}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 4,
                  justifyContent: 'center',
                }}
              >
                {filteredProducts.map((product, index) => (
                  <Box
                    key={product.id}
                    sx={{
                      flex: {
                        xs: '1 1 100%',
                        sm: '1 1 calc(50% - 32px)',
                        md: '1 1 calc(33.333% - 32px)',
                      },
                      maxWidth: { xs: '100%', sm: 'calc(50% - 32px)', md: 'calc(33.333% - 32px)' },
                    }}
                  >
                    <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                      <Card
                        elevation={0}
                        onMouseEnter={() => setHoveredCard(product.id)}
                        onMouseLeave={() => setHoveredCard(null)}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: 4,
                          overflow: 'hidden',
                          border: `1px solid ${theme.palette.divider}`,
                          transition: 'all 0.3s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: '0 8px 24px 0 rgba(0,0,0,0.1)',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            paddingTop: '100%',
                            overflow: 'hidden',
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={'/assets/img/card.png'}
                            alt={product.name}
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              transition: 'transform 0.3s ease-in-out',
                              transform: hoveredCard === product.id ? 'scale(1.05)' : 'scale(1)',
                            }}
                          />
                          {product.isNew && (
                            <Chip
                              label="Nouveau"
                              color="primary"
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: 16,
                                right: 16,
                                borderRadius: '50px',
                                fontWeight: 'bold',
                              }}
                            />
                          )}
                        </Box>
                        <CardContent sx={{ flexGrow: 1, p: 3 }}>
                          <Typography
                            variant="h6"
                            component="h3"
                            gutterBottom
                            sx={{ fontWeight: 'bold' }}
                          >
                            {product.name}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mt: 'auto',
                            }}
                          >
                            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                              {typeof product.price === 'number'
                                ? product.price.toFixed(2)
                                : '0.00'}{' '}
                              €
                            </Typography>
                            <Button
                              variant="contained"
                              onClick={() => addToCart(product)}
                              startIcon={<ShoppingCartIcon />}
                              sx={{
                                borderRadius: '50px',
                                px: 3,
                                py: 1,
                                fontWeight: 'bold',
                                boxShadow: '0 4px 14px 0 rgba(0,0,0,0.1)',
                                transition: 'all 0.3s ease-in-out',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 6px 20px 0 rgba(0,0,0,0.2)',
                                },
                              }}
                            >
                              Ajouter
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Zoom>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </PageTransition>
  );
};
